import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from '../../environment/environment';
import {
  SeminarAttendeeBackendModel,
  SeminarAttendeeModel,
  SeminarBackendModel,
  SeminarCreateUpdateModel,
  SeminarDateBackendModel,
  SeminarDateModel,
  SeminarDateSeriesBackendListModel,
  SeminarDateSeriesBackendModel,
  SeminarDateSeriesDetailsBackendModel,
  SeminarDateSeriesDetailsModel,
  SeminarDateSeriesListModel,
  SeminarDateSeriesModel,
  SeminarDeleteInfoBackendModel,
  SeminarDeleteInfoModel,
  SeminarListBackendModel,
  SeminarListModel,
  SeminarModel,
} from '../models/seminar.model';
import { TrainingCategoryService } from './training-category.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class SeminarService {
  constructor(
    private http: HttpClient,
    private userService: UserService,
    private courseTypeService: TrainingCategoryService
  ) {}

  public getSeminarById(
    id: number,
    includePastSeminars: boolean = false
  ): Observable<SeminarModel> {
    const formData = new FormData();
    formData.append('mode', 'getSeminar');
    formData.append('id', id.toString());
    formData.append('include_past_seminars', includePastSeminars ? '1' : '0');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: SeminarBackendModel;
      }>(environment.authority_short + 'seminar.php', formData)
      .pipe(
        map(response => this.parseSeminarBackendModel(response.data)),
        catchError(() => {
          return throwError(() => new Error('Seminar fetch failed'));
        })
      );
  }

  public getSeminars(
    includeDrafts: boolean = false,
    includePastSeminars: boolean = false
  ): Observable<SeminarDateSeriesListModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getAllSeminars');
    formData.append('include_drafts', includeDrafts ? '1' : '0');
    formData.append('include_past_seminars', includePastSeminars ? '1' : '0');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: SeminarDateSeriesBackendListModel[];
      }>(environment.authority_short + 'seminar.php', formData)
      .pipe(
        map(response =>
          response.data?.map(type => this.parseBackendListModel(type))
        ),
        catchError(error => {
          console.error('Error fetching seminars:', error);
          return throwError(() => new Error('Seminars fetch failed'));
        })
      );
  }

  public getSeminarDateSeriesById(
    id: number
  ): Observable<SeminarDateSeriesDetailsModel> {
    const formData = new FormData();
    formData.append('mode', 'getSeminarDateSeries');
    formData.append('id', id.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: SeminarDateSeriesDetailsBackendModel;
      }>(environment.authority_short + 'seminar.php', formData)
      .pipe(
        map(response => this.parseBackendModel(response.data)),
        catchError(() => {
          return throwError(() => new Error('seminar fetch failed'));
        })
      );
  }

  public getSeminarDateSeriesAttendees(
    seminarDateSeriesId: number
  ): Observable<SeminarAttendeeModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getSeminarDateSeriesAttendees');
    formData.append('id', seminarDateSeriesId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: SeminarAttendeeBackendModel[];
      }>(environment.authority_short + 'seminar.php', formData)
      .pipe(
        map(response =>
          response.data?.map(attendee =>
            this.parseSeminarAttendeeModel(attendee)
          )
        ),
        catchError(() => {
          return throwError(() => new Error('Seminar attendees fetch failed'));
        })
      );
  }

  public getSeminarsByCategory(
    categoryId: number
  ): Observable<SeminarDateSeriesListModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getSeminarsByCategory');
    formData.append('id_category', categoryId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: SeminarDateSeriesBackendListModel[];
      }>(environment.authority_short + 'seminar.php', formData)
      .pipe(
        map(response =>
          response.data?.map(type => this.parseBackendListModel(type))
        ),
        catchError(() => {
          return throwError(() => new Error('Seminars fetch failed'));
        })
      );
  }

  public getSeminarsByLecturer(
    lecturerId: number
  ): Observable<SeminarDateSeriesListModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getSeminarsByLecturer');
    formData.append('id_lecturer', lecturerId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: SeminarDateSeriesBackendListModel[];
      }>(environment.authority_short + 'seminar.php', formData)
      .pipe(
        map(response =>
          response.data?.map(type => this.parseBackendListModel(type))
        ),
        catchError(() => {
          return throwError(() => new Error('Seminars fetch failed'));
        })
      );
  }

  public getSeminarsByUser(
    email: string,
    includePastSeminars: boolean = false
  ): Observable<SeminarDateSeriesListModel[]> {
    const formData = new FormData();
    formData.append('mode', 'getSeminarsByUser');
    formData.append('email', email);
    formData.append('include_past_seminars', includePastSeminars ? '1' : '0');

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: SeminarDateSeriesBackendListModel[];
      }>(environment.authority_short + 'seminar.php', formData)
      .pipe(
        map(response =>
          response.data?.map(type => this.parseBackendListModel(type))
        ),
        catchError(() => {
          return throwError(() => new Error('Seminars fetch failed'));
        })
      );
  }

  public getSeminarDeleteInfo(
    seminarId: number
  ): Observable<SeminarDeleteInfoModel> {
    const formData = new FormData();
    formData.append('mode', 'getSeminarDeleteInfo');
    formData.append('id', seminarId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
        data: SeminarDeleteInfoBackendModel;
      }>(environment.authority_short + 'seminar.php', formData)
      .pipe(
        map(response => this.parseSeminarDeleteInfoBackendModel(response.data)),
        catchError(() => {
          return throwError(
            () => new Error('Seminar delete info fetch failed')
          );
        })
      );
  }

  public deleteSeminar(seminarId: number): Observable<boolean> {
    const formData = new FormData();
    formData.append('mode', 'deleteSeminar');
    formData.append('id', seminarId.toString());

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'seminar.php', formData)
      .pipe(
        map(response => response.success),
        catchError(() => {
          return throwError(() => new Error('Seminar delete failed'));
        })
      );
  }

  public cancelSeminar(
    seminarId: number,
    cancelReason: string
  ): Observable<boolean> {
    const formData = new FormData();
    formData.append('mode', 'cancelSeminar');
    formData.append('id', seminarId.toString());
    formData.append('cancel_reason', cancelReason);

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'seminar.php', formData)
      .pipe(
        map(response => response.success),
        catchError(() => {
          return throwError(() => new Error('Seminar cancel failed'));
        })
      );
  }

  public createSeminar(seminar: SeminarCreateUpdateModel): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'createSeminar');
    seminar.lecturerId &&
      formData.append('id_lecturer', seminar.lecturerId.toString());
    if (seminar.lecturer2Id !== undefined && seminar.lecturer2Id !== null) {
      formData.append('id_lecturer2', seminar.lecturer2Id.toString());
    }
    seminar.categoryId &&
      formData.append('id_category', seminar.categoryId.toString());
    seminar.category2Id &&
      formData.append('id_category2', seminar.category2Id.toString());
    formData.append('title', seminar.title);
    seminar.maxParticipants &&
      formData.append('max_participants', seminar.maxParticipants.toString());
    seminar.recommendation &&
      formData.append('recommendation', seminar.recommendation);
    if (seminar.requirement !== undefined && seminar.requirement !== null) {
      formData.append('requirement', seminar.requirement);
    }
    formData.append('modality', seminar.modality);
    if (seminar.link !== undefined && seminar.link !== null) {
      formData.append('link', seminar.link);
    }
    if (seminar.platform !== undefined && seminar.platform !== null) {
      formData.append('platform', seminar.platform);
    }
    if (seminar.meetingId !== undefined && seminar.meetingId !== null) {
      formData.append('meeting_id', seminar.meetingId);
    }
    if (
      seminar.meetingPassword !== undefined &&
      seminar.meetingPassword !== null
    ) {
      formData.append('meeting_password', seminar.meetingPassword);
    }
    if (seminar.address !== undefined && seminar.address !== null) {
      formData.append('address', JSON.stringify(seminar.address));
    }
    seminar.description && formData.append('description', seminar.description);
    seminar.about && formData.append('about', seminar.about);
    seminar.goals && formData.append('goals', seminar.goals);
    seminar.benefit && formData.append('benefit', seminar.benefit);
    seminar.seminarNr && formData.append('seminar_nr', seminar.seminarNr);
    seminar.fobiPoints &&
      formData.append('fobi_points', seminar.fobiPoints ? '1' : '0');
    seminar.conditions &&
      formData.append('conditions', JSON.stringify(seminar.conditions));
    formData.append(
      'paypal_payment_available',
      seminar.paypalPaymentAvailable ? '1' : '0'
    );
    seminar.additionsTitle &&
      formData.append('additions_title', seminar.additionsTitle);
    seminar.additionsDescription &&
      formData.append('additions_description', seminar.additionsDescription);
    seminar.specialInterest &&
      formData.append('special_interest', seminar.specialInterest);
    formData.append('is_draft', seminar.isDraft ? '1' : '0');
    formData.append('date_series', JSON.stringify(seminar.dateSeries));
    formData.append('file_ids', JSON.stringify(seminar.fileIds));
    formData.append('link_ids', JSON.stringify(seminar.linkIds));
    formData.append('sponsor_card_ids', JSON.stringify(seminar.sponsorCardIds));

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'seminar.php', formData)
      .pipe(
        map(() => undefined),
        catchError(() => {
          return throwError(() => new Error('Seminar creation failed'));
        })
      );
  }

  public updateSeminar(seminar: SeminarCreateUpdateModel): Observable<void> {
    const formData = new FormData();
    formData.append('mode', 'updateSeminar');
    formData.append('id', seminar.id.toString());
    seminar.lecturerId &&
      formData.append('id_lecturer', seminar.lecturerId.toString());
    if (seminar.lecturer2Id !== undefined && seminar.lecturer2Id !== null) {
      formData.append('id_lecturer2', seminar.lecturer2Id.toString());
    }
    seminar.categoryId &&
      formData.append('id_category', seminar.categoryId.toString());
    seminar.category2Id &&
      formData.append('id_category2', seminar.category2Id.toString());
    formData.append('title', seminar.title);
    seminar.maxParticipants &&
      formData.append('max_participants', seminar.maxParticipants.toString());
    seminar.recommendation &&
      formData.append('recommendation', seminar.recommendation);
    seminar.requirement && formData.append('requirement', seminar.requirement);

    seminar.modality && formData.append('modality', seminar.modality);
    seminar.link && formData.append('link', seminar.link);
    if (seminar.platform !== undefined && seminar.platform !== null) {
      formData.append('platform', seminar.platform);
    }
    if (seminar.meetingId !== undefined && seminar.meetingId !== null) {
      formData.append('meeting_id', seminar.meetingId);
    }
    if (
      seminar.meetingPassword !== undefined &&
      seminar.meetingPassword !== null
    ) {
      formData.append('meeting_password', seminar.meetingPassword);
    }
    if (seminar.address !== undefined && seminar.address !== null) {
      formData.append('address', JSON.stringify(seminar.address));
    }
    seminar.description && formData.append('description', seminar.description);
    seminar.about && formData.append('about', seminar.about);
    seminar.goals && formData.append('goals', seminar.goals);
    seminar.benefit && formData.append('benefit', seminar.benefit);
    seminar.seminarNr && formData.append('seminar_nr', seminar.seminarNr);
    seminar.fobiPoints &&
      formData.append('fobi_points', seminar.fobiPoints ? '1' : '0');
    seminar.conditions &&
      formData.append('conditions', JSON.stringify(seminar.conditions));
    formData.append(
      'paypal_payment_available',
      seminar.paypalPaymentAvailable ? '1' : '0'
    );

    seminar.additionsTitle &&
      formData.append('additions_title', seminar.additionsTitle);
    seminar.additionsDescription &&
      formData.append('additions_description', seminar.additionsDescription);
    seminar.specialInterest &&
      formData.append('special_interest', seminar.specialInterest);
    formData.append('is_draft', seminar.isDraft ? '1' : '0');
    formData.append('date_series', JSON.stringify(seminar.dateSeries));
    formData.append('file_ids', JSON.stringify(seminar.fileIds));
    formData.append('link_ids', JSON.stringify(seminar.linkIds));
    formData.append('sponsor_card_ids', JSON.stringify(seminar.sponsorCardIds));

    return this.http
      .post<{
        success: boolean;
        message: string;
      }>(environment.authority_short + 'seminar.php', formData)
      .pipe(
        map(() => undefined),
        catchError(() => {
          return throwError(() => new Error('Seminar update failed'));
        })
      );
  }

  public parseBackendListModel(
    seminar: SeminarDateSeriesBackendListModel
  ): SeminarDateSeriesListModel {
    return {
      dateSeriesId: seminar.id_date_series,
      dateSeriesName: seminar.date_series_name,
      seminar: {
        id: seminar.seminar.id,
        title: seminar.seminar.title,
        modality: seminar.seminar.modality,
        link: seminar.seminar.link,
        address: seminar.seminar.address && seminar.seminar.address,
        maxParticipants: seminar.seminar.max_participants,
        fobiPoints: Boolean(seminar.seminar.fobi_points),
        isDraft: Boolean(seminar.seminar.is_draft),
        isCanceled: Boolean(seminar.seminar.is_canceled),
      },
      lecturer1: this.userService.parseBackendListModel(seminar.lecturer1),
      lecturer2:
        seminar.lecturer2 &&
        this.userService.parseBackendListModel(seminar.lecturer2),
      category1: seminar.category1,
      category2: seminar.category2,
      dates: seminar.dates?.map(date => {
        return this.parseDateModel(date);
      }),
      participantCount: seminar.participant_count,
    };
  }

  public parseBackendModel(
    seminarDateSeries: SeminarDateSeriesDetailsBackendModel
  ): SeminarDateSeriesDetailsModel {
    return {
      dateSeriesId: seminarDateSeries.id_date_series,
      dateSeriesName: seminarDateSeries.date_series_name,
      seminar: {
        id: seminarDateSeries.seminar.id,
        title: seminarDateSeries.seminar.title,
        modality: seminarDateSeries.seminar.modality,
        link: seminarDateSeries.seminar.link,
        platform: seminarDateSeries.seminar.platform,
        meetingId: seminarDateSeries.seminar.meeting_id,
        meetingPassword: seminarDateSeries.seminar.meeting_password,
        address:
          seminarDateSeries.seminar.address &&
          seminarDateSeries.seminar.address,
        maxParticipants: seminarDateSeries.seminar.max_participants,
        recommendation: seminarDateSeries.seminar.recommendation,
        requirement: seminarDateSeries.seminar.requirement,
        description: seminarDateSeries.seminar.description,
        about: seminarDateSeries.seminar.about,
        goals: seminarDateSeries.seminar.goals,
        benefit: seminarDateSeries.seminar.benefit,
        seminarNr: seminarDateSeries.seminar.seminar_nr,
        conditions: seminarDateSeries.seminar.conditions,
        paypalPaymentAvailable: Boolean(
          seminarDateSeries.seminar.paypal_payment_available
        ),
        additionsTitle: seminarDateSeries.seminar.additions_title,
        additionsDescription: seminarDateSeries.seminar.additions_description,
        specialInterest: seminarDateSeries.seminar.special_interest,
        fobiPoints: Boolean(seminarDateSeries.seminar.fobi_points),
        isDraft: Boolean(seminarDateSeries.seminar.is_draft),
        isCanceled: Boolean(seminarDateSeries.seminar.is_canceled),
        cancelReason: seminarDateSeries.seminar.cancel_reason,
      },
      lecturer1: this.userService.parseBackendListModel(
        seminarDateSeries.lecturer1
      ),
      lecturer2:
        seminarDateSeries.lecturer2 &&
        this.userService.parseBackendListModel(seminarDateSeries.lecturer2),
      category1: this.courseTypeService.parseBackendModel(
        seminarDateSeries.category1
      ),
      category2:
        seminarDateSeries.category2 &&
        this.courseTypeService.parseBackendModel(seminarDateSeries.category2),
      dates: seminarDateSeries.dates.map(this.parseDateModel),
      participantCount: seminarDateSeries.participant_count,
      currentUserStatus: seminarDateSeries.current_user_status,
      sponsorCardIds: seminarDateSeries.sponsor_card_ids,
      waitlistAttendeeCount: seminarDateSeries.waitlist_attendee_count,
      waitlistBookingId: seminarDateSeries.waitlist_booking_id,
    };
  }

  private parseDateModel(date: SeminarDateBackendModel): SeminarDateModel {
    return {
      id: date.id,
      startDate: date.start_date ? new Date(date.start_date) : null,
      endDate: date.end_date ? new Date(date.end_date) : null,
      fobiPoints: date.fobi_points,
      isCanceled: date.is_canceled,
    };
  }

  private parseSeminarAttendeeModel(
    seminarAttendee: SeminarAttendeeBackendModel
  ): SeminarAttendeeModel {
    return {
      id: seminarAttendee.id,
      firstName: seminarAttendee.first_name,
      lastName: seminarAttendee.last_name,
      email: seminarAttendee.email,
      birthdate: seminarAttendee.birthdate
        ? new Date(seminarAttendee.birthdate)
        : null,
      phoneNumber: seminarAttendee.phone_number,
      jobTitle: seminarAttendee.job_title,
      condition: seminarAttendee.condition,
      conditionPrice: seminarAttendee.condition_price,
      status: seminarAttendee.status,
      createdAt:
        seminarAttendee.created_at && new Date(seminarAttendee.created_at),
      bookingNr: seminarAttendee.booking_nr,
      bookingId: seminarAttendee.booking_id,
      totalPrice:
        seminarAttendee.total_price != null
          ? Number(seminarAttendee.total_price)
          : undefined,
      discountedPrice:
        seminarAttendee.discounted_total_price != null
          ? Number(seminarAttendee.discounted_total_price)
          : undefined,
      couponCodes: seminarAttendee.coupon_codes,
      paymentMethod: seminarAttendee.payment_method,
    };
  }

  public parseSeminarBackendModel(seminar: SeminarBackendModel): SeminarModel {
    return {
      id: seminar.id,
      title: seminar.title,
      category1:
        seminar.category1 &&
        this.courseTypeService.parseBackendModel(seminar.category1),
      category2:
        seminar.category2 &&
        this.courseTypeService.parseBackendModel(seminar.category2),
      description: seminar.description,
      dateSeries: seminar.date_series?.map(dateSeries =>
        this.parseBackendDateSeriesModel(dateSeries)
      ),
      lecturer1:
        seminar.lecturer1 &&
        this.userService.parseBackendListModel(seminar.lecturer1),
      lecturer2:
        seminar.lecturer2 &&
        this.userService.parseBackendListModel(seminar.lecturer2),
      fobiPoints: Boolean(seminar.fobi_points),
      maxParticipants: seminar.max_participants,
      recommendation: seminar.recommendation,
      requirement: seminar.requirement,
      modality: seminar.modality,
      link: seminar.link,
      platform: seminar.platform ?? null,
      meetingId: seminar.meeting_id ?? null,
      meetingPassword: seminar.meeting_password ?? null,
      address: seminar.address && JSON.parse(seminar.address),
      about: seminar.about,
      goals: seminar.goals,
      benefit: seminar.benefit,
      seminarNr: seminar.seminar_nr,
      conditions: seminar.conditions
        ? [...seminar.conditions].sort((a, b) => {
            const order = [
              'regular',
              'early_bird',
              'office',
              'bed',
              'vdb',
              'logo',
              'vdp',
            ];
            const indexA = order.indexOf((a.type || '').toLowerCase());
            const indexB = order.indexOf((b.type || '').toLowerCase());
            // If not found, place at end
            return (
              (indexA === -1 ? order.length : indexA) -
              (indexB === -1 ? order.length : indexB)
            );
          })
        : [],
      paypalPaymentAvailable: Boolean(seminar.paypal_payment_available),
      additionsTitle: seminar.additions_title,
      additionsDescription: seminar.additions_description,
      specialInterest: seminar.special_interest,
      loaded: 6,
      image: '../../../assets/images/placeholder.svg',
      isDraft: seminar.is_draft,
      isCanceled: seminar.is_canceled,
      cancelReason: seminar.cancel_reason,
      sponsorCardIds: seminar.sponsor_card_ids,
    };
  }

  public parseSeminarListBackendModel(
    seminar: SeminarListBackendModel
  ): SeminarListModel {
    return {
      id: seminar.id,
      title: seminar.title,
      category1:
        seminar.category1 &&
        this.courseTypeService.parseBackendModel(seminar.category1),
      category2:
        seminar.category2 &&
        this.courseTypeService.parseBackendModel(seminar.category2),
      dateSeries: seminar.date_series.map(dateSeries =>
        this.parseBackendDateSeriesModel(dateSeries)
      ),
      lecturer1: this.userService.parseBackendListModel(seminar.lecturer1),
      lecturer2: this.userService.parseBackendListModel(seminar.lecturer2),
      fobiPoints: seminar.fobi_points && Boolean(seminar.fobi_points),
      maxParticipants: seminar.max_participants,
      modality: seminar.modality,
      link: seminar.link,
      address: seminar.address && JSON.parse(seminar.address),
      seminarNr: seminar.seminar_nr,
      loaded: 6,
      image: '../../../assets/images/placeholder.svg',
      isDraft: seminar.is_draft,
      isCanceled: seminar.is_canceled,
    };
  }

  private parseSeminarDeleteInfoBackendModel(
    deleteInfo: SeminarDeleteInfoBackendModel
  ): SeminarDeleteInfoModel {
    return {
      pendingDateSeriesCount: deleteInfo.pending_date_series_count,
      totalRegistrationCount: deleteInfo.total_registration_count,
      details:
        deleteInfo.details?.map(detail => ({
          dateSeriesName: detail.date_series_name,
          registrationCount: detail.registration_count,
        })) ?? [],
    };
  }

  public parseBackendDateSeriesModel(
    dateSeries: SeminarDateSeriesBackendModel
  ): SeminarDateSeriesModel {
    return {
      id: dateSeries.id,
      name: dateSeries.name,
      participantCount: dateSeries.participant_count,
      currentUserStatus: dateSeries.current_user_status,
      dates: dateSeries.dates.map(date => ({
        id: date.id,
        startDate: date.start_date ? new Date(date.start_date) : null,
        endDate: date.end_date ? new Date(date.end_date) : null,
        fobiPoints: date.fobi_points,
        isCanceled: date.is_canceled,
      })),
    };
  }
}
